import * as React from "react";
import { css } from "@emotion/react";
import isEqual from "react-fast-compare";
import { DetailItem } from "main/javascripts/components/spot/DetailItem";
import { DetailItemList } from "main/javascripts/components/spot/DetailItemList";
import { DetailItemText } from "main/javascripts/components/spot/DetailItemText";
import { HotelCheckin } from "main/javascripts/components/hotel/HotelCheckin";
import { HotelCheckout } from "main/javascripts/components/hotel/HotelCheckout";
import { Hotel } from "main/javascripts/types/hotel";
import {
  fontSize,
  fontWeight,
  lineHeight,
} from "main/javascripts/styles/base/typographyStyle";
import { space } from "main/javascripts/styles/base/spaceStyle";
import {
  borderColor,
  textColor,
} from "main/javascripts/styles/base/colorStyle";
import { CollapseDetailItem } from "main/javascripts/components/spot/CollapseDetailItem";
import { moreThanBreakpoint } from "main/javascripts/styles/base/responsiveStyle";
import { useTranslation } from "react-i18next";

export interface IProps {
  hotel: Hotel.Detail;
  styles?: any;
}

const HotelDetailsComponent: React.FC<IProps> = (
  props: IProps
): React.ReactElement => {
  const { hotel } = props;
  const { checkin, checkout } = hotel;
  const { t } = useTranslation(["component"]);

  if (!hotel.isEan) {
    return null;
  }

  const amenityNames: string[] = hotel.hotelAmenities.map(
    (amenity: Hotel.Amenity) => {
      return amenity.name;
    }
  );

  const hasFacilityDetails =
    hotel.roomDescription ||
    hotel.amenityDescription ||
    hotel.diningDescription;
  const hasLocationDetails =
    hotel.headlineDescription ||
    hotel.locationDescription ||
    hotel.attractionDescription;

  return (
    <div css={blockStyle}>
      <div css={groupBlockStyle}>
        <h2 css={headlineStyle}>{t("hotelShow.hotelFacilitiesAndServices")}</h2>
        <DetailItem title={t("hotelShow.facilitiesAndServices")}>
          <DetailItemList listData={amenityNames} />
        </DetailItem>
        {hasFacilityDetails && (
          <CollapseDetailItem>
            {hotel.roomDescription && (
              <DetailItemText>
                <div
                  css={detailTextStyle}
                  dangerouslySetInnerHTML={{
                    __html: hotel.roomDescription,
                  }}
                />
              </DetailItemText>
            )}
            {hotel.amenityDescription && (
              <DetailItemText>
                <div
                  css={detailTextStyle}
                  dangerouslySetInnerHTML={{
                    __html: hotel.amenityDescription,
                  }}
                />
              </DetailItemText>
            )}
            {hotel.diningDescription && (
              <DetailItemText>
                <div
                  dangerouslySetInnerHTML={{
                    __html: hotel.diningDescription,
                  }}
                />
              </DetailItemText>
            )}
          </CollapseDetailItem>
        )}
      </div>

      <div css={groupBlockStyle}>
        <h2 css={headlineStyle}>{t("hotelShow.hotelPolicies")}</h2>
        <HotelCheckin checkin={checkin} />
        <HotelCheckout checkout={checkout} />
        {hotel.policyDescription && (
          <DetailItem title={t("common.others")}>
            <DetailItemText>
              <div
                dangerouslySetInnerHTML={{
                  __html: hotel.policyDescription,
                }}
              />
            </DetailItemText>
          </DetailItem>
        )}
        {(hotel.feeDescription || hotel.mandatoryFeeDescription) && (
          <DetailItem title={t("hotelShow.serviceChargesAndOptionalFees")}>
            {hotel.feeDescription && (
              <DetailItemText>
                <div
                  dangerouslySetInnerHTML={{
                    __html: hotel.feeDescription,
                  }}
                />
              </DetailItemText>
            )}
            {hotel.mandatoryFeeDescription && (
              <DetailItemText>
                <div
                  dangerouslySetInnerHTML={{
                    __html: hotel.mandatoryFeeDescription,
                  }}
                />
              </DetailItemText>
            )}
          </DetailItem>
        )}
      </div>

      {hasLocationDetails && (
        <div css={groupBlockStyle}>
          <h2 css={headlineStyle}>{t("hotelShow.localGuide")}</h2>
          {hotel.locationDescription && (
            <DetailItem title={hotel.headlineDescription}>
              <DetailItemText>
                <div
                  dangerouslySetInnerHTML={{
                    __html: hotel.locationDescription,
                  }}
                />
              </DetailItemText>
            </DetailItem>
          )}
          {hotel.attractionDescription && (
            <CollapseDetailItem title={t("hotelShow.localInformation")}>
              <DetailItemText>
                <div
                  dangerouslySetInnerHTML={{
                    __html: hotel.attractionDescription,
                  }}
                />
              </DetailItemText>
            </CollapseDetailItem>
          )}
        </div>
      )}
    </div>
  );
};

const areEqual: any = (prevProps: any, nextProps: any): any => {
  return isEqual(prevProps, nextProps);
};
export const HotelDetails: any = React.memo(HotelDetailsComponent, areEqual);

const blockStyle = css`
  font-size: ${fontSize.caption};
  line-height: ${lineHeight.caption1};
`;
const groupBlockStyle = css`
  padding: ${space.atom} 0 ${space.atom1_5x};
  &:before {
    content: "";
    display: block;
    border-top: 1px solid ${borderColor.secondaryLightColor};
    margin: 0 ${space.atom} ${space.atom3x};
  }
  &:first-of-type:before {
    content: none;
  }
`;
const headlineStyle = css`
  line-height: ${lineHeight.headline2};
  padding: ${space.atom2x} ${space.atom};
  margin: 0;
  font-size: ${fontSize.mediumHeading};
  color: ${textColor.primaryDarkColor};
  font-weight: ${fontWeight.bold};
  text-align: center;
  letter-spacing: 0.2em;
  ${moreThanBreakpoint("desktop")} {
    font-size: ${fontSize.headline};
    font-weight: ${fontWeight.medium};
  }
`;
const detailTextStyle = css`
  padding-bottom: 1em;
`;
